/* =========================================================
 * Silicone Letter Selector v2 — Outdoor Paws custom engine
 * Web component <silicone-letter-selector>
 *
 * Palmy-Groomer-style flow (simpler for customers):
 *   1. Choose size  — Small (19mm) / Large (25mm)
 *   2. Choose a colour — the colour new taps use
 *   3. Tap letters (A–Z) and charms from ONE grid to build the collar
 *      · items append in order · tap a placed item to CYCLE its colour
 *      · remove with the ⌫ key or a tile's ×
 *
 * Reads live Shopify variant data rendered into <script class="sls-data">.
 * Data model = Outdoor Paws' 4-product setup (all in the letters collection):
 *   - "Silicone Collar Letters 19mm/25mm": option Letter (A–Z) × Colour
 *   - "Silicone Collar Charms 19mm/25mm":  option Charm (Flower/Heart/Paw) × Colour
 * size comes from the product (19mm/25mm tag) so letter/charm+colour+size maps
 * to exactly one variant and inventory deducts per selection.
 *
 * Public contract (used by <complete-walk-set>):
 *   - getLetterItems() -> { items, valid, error }
 *   - "sls:change" event -> detail { valid, lineItems, breakdown, name, size }
 *   - "sls:add-request" cancelable event (host can intercept the standalone add)
 *   - attrs: data-free-letters, data-max-letters, data-embedded, data-fixed-size
 *   - setFixedSize(size) — lock size from a host (e.g. the bundle's collar size)
 * ========================================================= */

(function () {
  'use strict';

  // Real Shopify option value -> swatch hex (pastel, matches the brand).
  var COLOUR_HEX = {
    'Purple': '#c3aee0',
    'Dark Blue': '#8aa4d6',
    'Light Blue': '#a9d3ef',
    'Pink': '#f4a9c4',
    'Yellow': '#f2d98b'
  };
  var COLOUR_ORDER = ['Yellow', 'Light Blue', 'Purple', 'Pink', 'Dark Blue'];
  // Friendly display names (the real Shopify option value stays "Purple" etc.)
  var COLOUR_LABEL = {
    'Purple': 'Lilac',
    'Dark Blue': 'Ocean',
    'Light Blue': 'Sea Salt',
    'Pink': 'Bubblegum',
    'Yellow': 'Butter'
  };
  var SIZE_ORDER = ['19mm', '25mm'];
  var SIZE_LABEL = { '19mm': 'Small', '25mm': 'Large' };
  var CHARM_ORDER = ['Flower', 'Heart', 'Paw Print', 'Star'];
  var ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  function money(cents, format) {
    var value = (cents / 100).toFixed(2);
    if (format && format.indexOf('{{') !== -1) {
      return format.replace(/\{\{\s*amount\s*\}\}/, value)
                   .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100))
                   .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, value.replace('.', ','));
    }
    return '$' + value;
  }
  function rank(list, v) { var i = list.indexOf(v); return i === -1 ? 99 : i; }

  class SiliconeLetterSelector extends HTMLElement {
    connectedCallback() {
      var dataEl = this.querySelector('.sls-data');
      if (!dataEl) return;
      try { this.data = JSON.parse(dataEl.textContent); }
      catch (e) { console.error('[sls] bad data', e); return; }

      this.freeLetters = parseInt(this.getAttribute('data-free-letters'), 10) || 0;
      // When embedded in the bundle, always trust the bundle's free-letter allowance
      // (avoids a stale/zero attribute on the snippet leaving letters charged).
      var slsHost = this.closest('complete-walk-set');
      if (slsHost && slsHost.getAttribute('data-free-letters')) {
        var slsHostFree = parseInt(slsHost.getAttribute('data-free-letters'), 10);
        if (!isNaN(slsHostFree)) this.freeLetters = slsHostFree;
      }
      this.maxLetters = parseInt(this.getAttribute('data-max-letters'), 10) || 15;
      this.embedded = this.getAttribute('data-embedded') === 'true';
      this.fixedSize = this.getAttribute('data-fixed-size') || null;
      this.moneyFormat = this.data.moneyFormat;

      // Build variant maps: lv[letter][colour][size], cv[charm][colour][size]
      this.lv = {}; this.cv = {};
      var sizeSet = {}, colourSet = {}, letterSet = {}, charmSet = {};
      (this.data.products || []).forEach(function (p) {
        var target = p.kind === 'charm' ? this.cv : this.lv;
        var keySet = p.kind === 'charm' ? charmSet : letterSet;
        (p.variants || []).forEach(function (v) {
          if (v.key == null || v.colour == null) return;
          if (!target[v.key]) target[v.key] = {};
          if (!target[v.key][v.colour]) target[v.key][v.colour] = {};
          target[v.key][v.colour][p.size] = v;
          sizeSet[p.size] = true; colourSet[v.colour] = true; keySet[v.key] = true;
        });
      }, this);

      this.sizes = Object.keys(sizeSet).sort(function (a, b) { return rank(SIZE_ORDER, a) - rank(SIZE_ORDER, b); });
      this.colours = Object.keys(colourSet).sort(function (a, b) { return rank(COLOUR_ORDER, a) - rank(COLOUR_ORDER, b); });
      this.letterSet = letterSet;
      this.charmList = Object.keys(charmSet).sort(function (a, b) { return rank(CHARM_ORDER, a) - rank(CHARM_ORDER, b); });

      // State
      this.size = this.fixedSize || this.sizes[0] || null;
      this.defaultColour = this.colours[0] || null;
      this.seq = [];   // ordered [{type:'letter', ch, colour} | {type:'charm', name, colour}]

      this._cacheEls();
      this._renderSizes();
      this._renderDefaultColours();
      this._renderGrid();
      this._renderTray();
      this._bind();
      this._reveal();
      this._update();
    }

    /* ---------- data helpers ---------- */
    variantForLetter(ch, colour, size) { return (this.lv[ch] && this.lv[ch][colour] && this.lv[ch][colour][size]) || null; }
    variantForCharm(name, colour, size) { return (this.cv[name] && this.cv[name][colour] && this.cv[name][colour][size]) || null; }
    _variantFor(it, colour) {
      colour = colour || it.colour;
      return it.type === 'charm' ? this.variantForCharm(it.name, colour, this.size) : this.variantForLetter(it.ch, colour, this.size);
    }
    hex(colour) { return COLOUR_HEX[colour] || '#bbb'; }
    label(colour) { return COLOUR_LABEL[colour] || colour; }
    sizeLabel(sz) { return SIZE_LABEL[sz] || sz; }
    letterCount() { return this.seq.filter(function (it) { return it.type === 'letter'; }).length; }
    nameString() { return this.seq.filter(function (it) { return it.type === 'letter'; }).map(function (it) { return it.ch; }).join(''); }

    // Colours in which a given key is currently available for the chosen size.
    coloursFor(it) {
      return this.colours.filter(function (c) { var v = this._variantFor(it, c); return v && v.available; }, this);
    }
    firstColourFor(it) {
      if (this.defaultColour) { var v = this._variantFor(it, this.defaultColour); if (v && v.available) return this.defaultColour; }
      var avail = this.coloursFor(it); return avail[0] || this.defaultColour || this.colours[0] || null;
    }

    /* ---------- element cache ---------- */
    _cacheEls() {
      this.$sizeWrap = this.querySelector('[data-sizes]');
      this.$sizeRow = this.querySelector('[data-size-row]');
      this.$defWrap = this.querySelector('[data-default]');
      this.$defRow = this.querySelector('[data-default-row]');
      this.$tray = this.querySelector('[data-tray]');
      this.$trayItems = this.querySelector('[data-tray-items]');
      this.$trayLines = this.querySelector('[data-tray-lines]');
      this.$clear = this.querySelector('[data-clear]');
      this.$gridWrap = this.querySelector('[data-gridwrap]');
      this.$grid = this.querySelector('[data-grid]');
      this.$summary = this.querySelector('.sls__summary');
      this.$hint = this.querySelector('[data-hint]');
      var host = this.closest('complete-walk-set') || this.closest('.sls-section') || document;
      this.$collar = host ? host.querySelector('[data-sls-collar]') : null;
    }

    /* ---------- reveal / gating ---------- */
    _reveal() {
      var haveSize = !!this.size;
      if (this.$sizeWrap) this.$sizeWrap.hidden = !!this.fixedSize || this.sizes.length <= 1;
      if (this.$defWrap) this.$defWrap.hidden = !haveSize;
      if (this.$gridWrap) this.$gridWrap.hidden = !haveSize;
      if (this.$tray) this.$tray.hidden = !this.seq.length;
    }

    /* ---------- size ---------- */
    setFixedSize(size) {
      if (!size) return;
      this.fixedSize = size; this.size = size;
      if (this.$sizeWrap) this.$sizeWrap.hidden = true;
      this._renderGrid(); this._renderTray(); this._reveal(); this._update();
    }

    _renderSizes() {
      if (!this.$sizeRow) return;
      this.$sizeRow.innerHTML = '';
      this.sizes.forEach(function (sz) {
        var b = document.createElement('button');
        b.type = 'button'; b.className = 'sls2__size-btn';
        b.setAttribute('role', 'radio');
        b.setAttribute('aria-checked', sz === this.size ? 'true' : 'false');
        b.innerHTML = '<strong>' + this.sizeLabel(sz) + '</strong><small>' + sz + '</small>';
        b.addEventListener('click', function () {
          this.size = sz;
          // Re-validate every placed item against the new size.
          this.seq.forEach(function (it) {
            if (!this._variantFor(it)) { var c = this.firstColourFor(it); if (c) it.colour = c; }
          }, this);
          this._renderSizes(); this._renderDefaultColours(); this._renderGrid(); this._renderTray(); this._reveal(); this._update();
        }.bind(this));
        this.$sizeRow.appendChild(b);
      }, this);
    }

    /* ---------- default colour ---------- */
    _renderDefaultColours() {
      if (!this.$defRow) return;
      this.$defRow.innerHTML = '';
      this.colours.forEach(function (colour) {
        var wrap = document.createElement('button');
        wrap.type = 'button'; wrap.className = 'sls2__def-swatch';
        wrap.setAttribute('aria-checked', colour === this.defaultColour ? 'true' : 'false');
        wrap.title = this.label(colour);
        wrap.innerHTML = '<span class="sls2__def-dot" style="background:' + this.hex(colour) + '"></span><span class="sls2__def-name">' + this.label(colour) + '</span>';
        wrap.addEventListener('click', function () {
          this.defaultColour = colour;
          // Sets the colour for the NEXT item only. Tap a placed letter/charm to recolour just that one.
          this._renderDefaultColours();
          this._renderTray();
          this._update();
        }.bind(this));
        this.$defRow.appendChild(wrap);
      }, this);
    }

    /* ---------- grid (A–Z + charms + backspace) ---------- */
    _charmGlyphHTML(name, colour) {
      var c = colour || 'currentColor';
      if (name === 'Heart') return '<span class="sls2__glyph" style="color:' + c + '">♥</span>';
      if (name === 'Flower') return '<span class="sls2__glyph" style="color:' + c + '">✿</span>';
      if (name === 'Star') return '<span class="sls2__glyph" style="color:' + c + '">★</span>';
      return '<svg class="sls2__paw" viewBox="0 0 24 24" fill="' + c + '" aria-hidden="true"><circle cx="7" cy="8.5" r="2.6"/><circle cx="12" cy="6.4" r="2.6"/><circle cx="17" cy="8.5" r="2.6"/><path d="M12 11c-3.2 0-5.4 2.4-5.4 4.8C6.6 17.6 9 18.4 12 18.4s5.4-.8 5.4-2.6C17.4 13.4 15.2 11 12 11z"/></svg>';
    }

    _renderGrid() {
      if (!this.$grid) return;
      this.$grid.innerHTML = '';
      var atMax = this.letterCount() >= this.maxLetters;

      ALPHABET.forEach(function (ch) {
        var b = document.createElement('button');
        b.type = 'button'; b.className = 'sls2__key sls2__key--letter'; b.textContent = ch;
        var stocked = !!this.letterSet[ch];
        var anyColour = stocked && this.coloursFor({ type: 'letter', ch: ch }).length > 0;
        if (!anyColour) { b.disabled = true; b.classList.add('is-out'); b.title = 'Out of stock'; }
        else if (atMax) { b.disabled = true; b.title = 'Maximum letters reached'; }
        else { b.addEventListener('click', this._addLetter.bind(this, ch)); }
        this.$grid.appendChild(b);
      }, this);

      // Charms
      this.charmList.forEach(function (name) {
        var b = document.createElement('button');
        b.type = 'button'; b.className = 'sls2__key sls2__key--charm'; b.title = 'Add ' + name;
        b.innerHTML = this._charmGlyphHTML(name, '#7a6d5e');
        var anyColour = this.coloursFor({ type: 'charm', name: name }).length > 0;
        if (!anyColour) { b.disabled = true; b.classList.add('is-out'); }
        else b.addEventListener('click', this._addCharm.bind(this, name));
        this.$grid.appendChild(b);
      }, this);

      // Backspace
      var bk = document.createElement('button');
      bk.type = 'button'; bk.className = 'sls2__key sls2__key--back'; bk.title = 'Remove last';
      bk.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z"/><line x1="18" y1="9" x2="12" y2="15"/><line x1="12" y1="9" x2="18" y2="15"/></svg>';
      bk.disabled = !this.seq.length;
      bk.addEventListener('click', function () { this.seq.pop(); this._renderGrid(); this._renderTray(); this._reveal(); this._update(); }.bind(this));
      this.$grid.appendChild(bk);
    }

    _addLetter(ch) {
      if (this.letterCount() >= this.maxLetters) return;
      var it = { type: 'letter', ch: ch, colour: null };
      it.colour = this.firstColourFor(it);
      if (!it.colour) return;
      this.seq.push(it);
      this._renderGrid(); this._renderTray(); this._reveal(); this._update();
    }
    _addCharm(name) {
      var it = { type: 'charm', name: name, colour: null };
      it.colour = this.firstColourFor(it);
      if (!it.colour) return;
      this.seq.push(it);
      this._renderGrid(); this._renderTray(); this._reveal(); this._update();
    }

    /* ---------- tray (preview of the built collar) ---------- */
    _renderTray() {
      if (!this.$trayItems) return;
      this.$trayItems.innerHTML = '';
      this.seq.forEach(function (it, i) {
        var tile = document.createElement('div');
        tile.className = 'sls2__chip sls2__chip--' + it.type;
        tile.style.setProperty('--chip', this.hex(it.colour));

        var face = document.createElement('button');
        face.type = 'button'; face.className = 'sls2__chip-face';
        face.title = 'Tap to change colour (' + this.label(it.colour) + ')';
        face.innerHTML = it.type === 'charm' ? this._charmGlyphHTML(it.name, '#ffffff') : it.ch;
        face.addEventListener('click', this._cycleColour.bind(this, i));
        tile.appendChild(face);

        var rm = document.createElement('button');
        rm.type = 'button'; rm.className = 'sls2__chip-x'; rm.title = 'Remove'; rm.setAttribute('aria-label', 'Remove');
        rm.innerHTML = '&times;';
        rm.addEventListener('click', function (e) { e.stopPropagation(); this.seq.splice(i, 1); this._renderGrid(); this._renderTray(); this._reveal(); this._update(); }.bind(this));
        tile.appendChild(rm);

        this.$trayItems.appendChild(tile);
      }, this);
    }

    _cycleColour(index) {
      var it = this.seq[index]; if (!it) return;
      var avail = this.coloursFor(it);
      if (avail.length <= 1) return;
      var cur = avail.indexOf(it.colour);
      it.colour = avail[(cur + 1) % avail.length];
      this._renderTray(); this._update();
    }

    /* ---------- live collar preview ---------- */
    _darken(hex, amt) {
      if (!hex || hex.charAt(0) !== '#') return 'rgba(0,0,0,0.35)';
      var n = parseInt(hex.slice(1), 16);
      return 'rgb(' + Math.max(0, ((n >> 16) & 255) - amt) + ',' + Math.max(0, ((n >> 8) & 255) - amt) + ',' + Math.max(0, (n & 255) - amt) + ')';
    }
    // Charm glyph styled for the collar strap (3D shadow via --cd on the wrapper).
    _collarCharmHTML(name) {
      if (name === 'Heart') return '<span class="sls-collar__glyph">♥</span>';
      if (name === 'Flower') return '<span class="sls-collar__glyph">✿</span>';
      if (name === 'Star') return '<span class="sls-collar__glyph">★</span>';
      return '<svg class="sls-collar__paw" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="7" cy="8.5" r="2.6"/><circle cx="12" cy="6.4" r="2.6"/><circle cx="17" cy="8.5" r="2.6"/><path d="M12 11c-3.2 0-5.4 2.4-5.4 4.8C6.6 17.6 9 18.4 12 18.4s5.4-.8 5.4-2.6C17.4 13.4 15.2 11 12 11z"/></svg>';
    }

    _renderCollar() {
      if (!this.$collar) return;
      var sz = (this.size && this.size.indexOf('25') !== -1) ? 'sz25' : 'sz19';
      if (!this.seq.length) {
        this.$collar.innerHTML =
          '<div class="sls-collar sls-collar--' + sz + '"><div class="sls-collar__strap sls-collar__strap--empty">' +
          '<span class="sls-collar__ring"></span><span class="sls-collar__hint">Tap letters to see them on the collar ✨</span>' +
          '<span class="sls-collar__buckle"></span></div><p class="sls-collar__caption">Live collar preview</p></div>';
        return;
      }
      var inner = '';
      this.seq.forEach(function (it) {
        var c = this.hex(it.colour);
        if (it.type === 'letter') {
          inner += '<span class="sls-collar__letter" style="color:' + c + ';--cd:' + this._darken(c, 45) + '">' + it.ch + '</span>';
        } else {
          // Charms render in their chosen colour (like the letters), with a dark 3D edge so they stay readable.
          var cc = this.hex(it.colour);
          inner += '<span class="sls-collar__charm" style="color:' + cc + ';--cd:' + this._darken(cc, 55) + '">' + this._collarCharmHTML(it.name) + '</span>';
        }
      }, this);
      this.$collar.innerHTML =
        '<div class="sls-collar sls-collar--' + sz + '"><div class="sls-collar__strap">' +
        '<span class="sls-collar__ring"></span><div class="sls-collar__row">' + inner + '</div>' +
        '<span class="sls-collar__buckle"></span></div>' +
        '<p class="sls-collar__caption">Live collar preview · ' + this.sizeLabel(this.size) + '</p></div>';
    }

    /* ---------- pricing ---------- */
    _priceBreakdown() {
      var unit = null, charmUnit = null, paidCount = 0, lettersPaidTotal = 0, charmsTotal = 0, letterCount = 0, charmCount = 0;
      this.seq.forEach(function (it) {
        var v = this._variantFor(it);
        if (!v) return;
        if (it.type === 'letter') {
          if (unit == null) unit = v.price;
          letterCount++;
          if (letterCount > this.freeLetters) { paidCount++; lettersPaidTotal += v.price; }
        } else {
          if (charmUnit == null) charmUnit = v.price;
          charmCount++;
          charmsTotal += v.price;
        }
      }, this);
      return {
        unit: unit, charmUnit: charmUnit, count: letterCount, paidCount: paidCount,
        freeUsed: Math.min(letterCount, this.freeLetters),
        lettersPaidTotal: lettersPaidTotal, charmsTotal: charmsTotal, charms: charmCount,
        total: lettersPaidTotal + charmsTotal
      };
    }

    // Update the free-letter allowance at runtime (e.g. the bundle host syncing its setting).
    setFreeLetters(n) {
      n = parseInt(n, 10);
      if (isNaN(n)) return;
      this.freeLetters = n;
      this._update();
    }

    _trayLine(label, value, cls) {
      return '<div class="sls2__line' + (cls ? ' ' + cls : '') + '"><span>' + label + '</span><span>' + value + '</span></div>';
    }

    _lineItems() {
      var items = [], name = this.nameString();
      var groupId = 'sls-' + Math.abs(this._hash(name + ':' + this.seq.length));
      for (var k = 0; k < this.seq.length; k++) {
        var it = this.seq[k];
        var v = this._variantFor(it);
        if (!v || !v.available) {
          if (it.type === 'letter') return { error: 'A letter is out of stock in that colour/size. Tap it to change colour, or remove it.' };
          continue;
        }
        var props = { '_pos': String(k + 1), '_sls_group': groupId }; if (name) { props["Dog's name"] = name; } items.push({ id: v.id, quantity: 1, properties: props });
      }
      return { items: items };
    }

    getLetterItems() {
      if (!this.seq.length) return { items: [], valid: true };
      var built = this._lineItems();
      if (built.error) return { items: [], valid: false, error: built.error };
      return { items: built.items, valid: true };
    }

    /* ---------- update ---------- */
    _update() {
      var b = this._priceBreakdown();
      var valid = this.seq.length > 0;
      this.seq.forEach(function (it) { var v = this._variantFor(it); if (!v || !v.available) valid = false; }, this);

      // Tray breakdown — itemised lines (first N free / extra letters / charms)
      if (this.$trayLines) {
        var lines = '';
        if (!this.seq.length) {
          lines = '<div class="sls2__line sls2__line--muted"><span>Nothing added yet</span><span></span></div>';
        } else {
          var lunit = b.unit || 0;
          var cunit = b.charmUnit || 0;
          if (b.count > 0) {
            if (this.freeLetters > 0) {
              lines += this._trayLine('Letters (' + b.count + ')',
                '<span class="sls2__free">' + b.freeUsed + ' FREE</span>');
              if (b.paidCount > 0) {
                lines += this._trayLine('Extra letters (' + b.paidCount + ' &times; ' + money(lunit, this.moneyFormat) + ')',
                  money(b.lettersPaidTotal, this.moneyFormat));
              }
            } else {
              lines += this._trayLine('Letters (' + b.count + ' &times; ' + money(lunit, this.moneyFormat) + ')',
                money(b.count * lunit, this.moneyFormat));
            }
          }
          if (b.charms > 0) {
            lines += this._trayLine('Charms (' + b.charms + ' &times; ' + money(cunit, this.moneyFormat) + ')',
              money(b.charmsTotal, this.moneyFormat));
          }
          // Standalone page shows a running total here; the bundle shows the full total at the top of the page.
          if (!this.embedded) {
            lines += this._trayLine('Total', money(b.total, this.moneyFormat), 'sls2__line--total');
          }
        }
        this.$trayLines.innerHTML = lines;
      }

      // Standalone summary + add button (skipped when embedded in the bundle)
      if (this.$summary) {
        if (this.embedded) {
          this.$summary.innerHTML = '';
        } else {
          var label = valid ? 'Add to cart — ' + money(b.total, this.moneyFormat) : 'Tap a letter to begin';
          this.$summary.innerHTML =
            '<button type="button" class="sls__add"' + (valid ? '' : ' disabled') + '>' + label + '</button>';
          var btn = this.$summary.querySelector('.sls__add');
          if (btn) btn.addEventListener('click', this._addToCart.bind(this));
        }
      }

      this._renderCollar();

      this.dispatchEvent(new CustomEvent('sls:change', {
        bubbles: true,
        detail: { valid: valid, lineItems: valid ? this._lineItems().items : [], breakdown: b, name: this.nameString(), size: this.size }
      }));
    }

    /* ---------- add to cart ---------- */
    _addToCart(evt) {
      if (evt) evt.preventDefault();
      var built = this._lineItems();
      if (built.error) { this._error(built.error); return; }
      if (!built.items.length) return;
      var proceed = this.dispatchEvent(new CustomEvent('sls:add-request', { bubbles: true, cancelable: true, detail: { lineItems: built.items } }));
      if (!proceed) return; // a host handled the add
      this.submit(built.items);
    }

    submit(items) {
      var btn = this.$summary && this.$summary.querySelector('.sls__add');
      if (btn) { btn.classList.add('is-loading'); btn.disabled = true; }
      var routes = (window.theme && theme.routes) || {};
      fetch(routes.cartAdd || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items })
      })
      .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, body: j }; }); })
      .then(function (res) { if (!res.ok) throw new Error((res.body && res.body.description) || 'Could not add to cart'); this._afterAdd(); }.bind(this))
      .catch(function (e) { this._error(e.message); }.bind(this))
      .then(function () { if (btn) { btn.classList.remove('is-loading'); btn.disabled = false; } });
    }

    _afterAdd() {
      var cartType = (window.theme && theme.settings && theme.settings.cartType) || 'drawer';
      if (cartType === 'drawer') {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
      } else {
        window.location = (window.theme && theme.routes && theme.routes.cart) || '/cart';
      }
    }

    _error(msg) { if (this.$hint) { this.$hint.textContent = msg; this.$hint.classList.add('sls__hint--error'); } }

    _bind() {
      if (this.$clear) this.$clear.addEventListener('click', function () { this.seq = []; this._renderGrid(); this._renderTray(); this._reveal(); this._update(); }.bind(this));
    }

    _hash(str) { var h = 0; for (var i = 0; i < str.length; i++) { h = (h << 5) - h + str.charCodeAt(i); h |= 0; } return h; }
  }

  if (!window.customElements.get('silicone-letter-selector')) {
    window.customElements.define('silicone-letter-selector', SiliconeLetterSelector);
  }
})();
